import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { FilesService } from '@services/files.service';
import { IIntegratonSensor } from '@services/integration-sensors.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { OrganisationDataModelNode } from '@services/organisation-data.service';
import { DataPoint, IDataPoint } from '@services/datapoints-data.service';

export class AddDataModelNodeDialogData {
	ParentNode: OrganisationDataModelNode;
	NodeTypes: { text: string, value: number }[];
	Sensors: IIntegratonSensor[];
}

@Component({
	selector: 'app-add-data-model-node',
	templateUrl: './add-data-model-node.component.html',
	styleUrls: ['./add-data-model-node.component.scss']
})
export class AddDataModelNodeComponent {

	/** Form Group */
	dialogForm: FormGroup;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** parentNode */
	public parentNode: OrganisationDataModelNode;

	/** nodeTypeFields */
	public nodeTypeFields: Object = { text: "text", value: "value" };

	/** nodeTypes */
	public nodeTypes: { text: string, value: number }[] = [];

	/** selectedNodeType */
	public selectedNodeType: number = 0;

	/** sensors */
	public sensors: IIntegratonSensor[] = [];

	/** defaultNodeType */
	public defaultNodeType: number = 0;

	/** selectedSensors */
	public selectedSensors: IIntegratonSensor[] = [];

	/** sensorNodes */
	public sensorNodes: {name: string, description: string, datapointIDs: string}[] = [];

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param globalFunctions 
	 * @param filesService 
	 * @param siteSubscriptions 
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<AddDataModelNodeComponent>,
		private globalFunctions: GlobalFunctions,
		private filesService: FilesService,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: AddDataModelNodeDialogData,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account]) => !!account)
		).subscribe({
			next: ([account]) => {
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false); }
		});
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit(): void { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.parentNode = this.dialogData.ParentNode;
		this.nodeTypes = this.dialogData.NodeTypes;
		this.sensors = this.dialogData.Sensors;

		this.dialogForm = this.fb.group({
			Name: ["", Validators.required],
			Description: [""],
			NodeType: [this.defaultNodeType, [Validators.required]],
			DatapointIDs: [""],
			SensorNodes: [[]],
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Stop creating node?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * nodeTypeChange
	 * @param event event 
	 */
	nodeTypeChange(event: any) {
		this.selectedNodeType = event.value;

		this.dialogForm.get("Name")?.setValue("");
		this.dialogForm.get("Description")?.setValue("");
		this.dialogForm.get("DatapointIDs")?.setValue("");
	}

	/**
	 * sensorSelected
	 * @param sensor sensor 
	 */
	rebuildSensorNodes() {
		this.dialogForm.get("Name")?.setValue("Multiple Nodes");
		this.sensorNodes = this.selectedSensors.map((sensor: IIntegratonSensor) => (
			{
				name: sensor.Name, 
				description: sensor.Description, 
				datapointIDs: sensor.Datapoints.map((d: DataPoint) => d.DataPointID).toString()
			}
		));
		this.dialogForm.get("SensorNodes")?.setValue(this.sensorNodes);
		this.dialogForm.updateValueAndValidity();
	}

	/**
	 * toggleSensor
	 * @param event event 
	 * @param sensor sensor 
	 */
	toggleSensor(event: any, sensor: IIntegratonSensor) {
		if (event.checked) {
			this.selectedSensors.push(sensor);
		} else {
			this.selectedSensors = this.selectedSensors.filter((x: IIntegratonSensor) => x.SensorID != sensor.SensorID);
		}
		this.rebuildSensorNodes();
	}
}
